import React, {Fragment, Component} from 'react';
import PropTypes from 'prop-types';
import update from 'immutability-helper';
import {Icon, ThemeContext} from '../';
import ChatTabs from './ChatTabs';
import MessageField from './MessageField';
import MessageItem from './MessageItem';
import ChatService from '../../../utils/chat';

export default class Chat extends Component {
  static tabs = [
    {title: 'All', messagesFilter: item => (Boolean(!item.isTeamMessage))},
    {title: 'Team', messagesFilter: item => (Boolean(item.isTeamMessage))},
  ];

  static propTypes = {
    isDisplayed: PropTypes.bool.isRequired,
    onChatToggle: PropTypes.func.isRequired,
    channelId: PropTypes.string.isRequired,
    team: PropTypes.string.isRequired,
    user: PropTypes.shape().isRequired,
    isAudience: PropTypes.bool.isRequired,
  };

  constructor(props) {
    super(props);
    const {channelId, team, isAudience} = props;

    this.chatService = new ChatService(channelId);
    if (!isAudience) {
      this.teamChatService = new ChatService(`${channelId}_${team}`);
    }

    this.state = {
      activeTabIndex: 0,
      isDisplayed: true,
      messages: [],
    };
  }

  componentDidMount() {
    const {isAudience} = this.props;

    this.chatService.init({
      onHistory: this.onHistory,
      onMessage: this.onReceiveMessage,
    });

    if (!isAudience) {
      this.teamChatService.init({
        onHistory: this.onHistory,
        onMessage: this.onReceiveMessage,
      });
    }
  }

  componentDidUpdate(oldProps) {
    const {isDisplayed: isOldDisplayed} = oldProps;
    const {isDisplayed} = this.props;
    if (isOldDisplayed === false && isDisplayed) {
      this.scrollToBottom();
    }
  }

  componentWillUnmount() {
    const {isAudience} = this.props;

    this.chatService.unSubscribe();

    if (!isAudience) {
      this.teamChatService.unSubscribe();
    }
  }

  onHistory = (messages) => {
    const state = update(this.state, {messages: {$set: messages}});
    this.setState(state, this.scrollToBottom);
  };

  onTabChanged = (activeTabIndex) => {
    this.setState({activeTabIndex});
  };

  onSendPublicMessage = (message) => {
    const {user: currentUser, isAudience} = this.props;
    this.chatService.sendMessage({
      user: {
        name: currentUser.fullName,
        id: currentUser.id,
        image: currentUser.image,
      },
      type: isAudience ? 'audience' : 'player',
      sendAt: new Date(),
      message,
    });
  };

  onSendTeamMessage = (message) => {
    const {user: currentUser} = this.props;

    this.teamChatService.sendMessage({
      user: {
        name: currentUser.fullName,
        id: currentUser.id,
        image: currentUser.image,
      },
      type: 'player',
      isTeamMessage: true,
      sendAt: new Date(),
      message,
    });
  };

  onSendMessage = (message) => {
    const {activeTabIndex} = this.state;
    const {isAudience} = this.props;
    const isForTeamChat = Boolean(isAudience ? 0 : activeTabIndex);
    const chatMessageSender = isForTeamChat ? this.onSendTeamMessage : this.onSendPublicMessage;

    chatMessageSender(message);
  };

  onReceiveMessage = (message) => {
    const newState = update(this.state, {messages: {$unshift: [message]}});
    this.setState(newState, this.scrollToBottom);
  };

  onChatToggle = () => {
    const {isDisplayed, onChatToggle} = this.props;
    onChatToggle(!isDisplayed);
  };

  activeTab = () => {
    const {activeTabIndex} = this.state;
    const {tabs} = Chat;

    return tabs[activeTabIndex];
  };

  scrollToBottom = () => {
    if (!this.chatScroll) {
      return false;
    }

    const {scrollHeight} = this.chatScroll;
    this.chatScroll.scrollTop = scrollHeight;
    return true;
  };

  renderMessage = (item, index) => {
    const {user: {id: userId}} = this.props;
    const {message, user = {}, type, sendAt} = item;
    const {name, image, id} = user;
    const key = `_${index}`;
    const isSelf = userId === id;

    return (
      <MessageItem
        message={message}
        userName={name}
        userImage={image}
        sendAt={sendAt}
        self={isSelf}
        key={key}
        type={type}
      />
    );
  };

  renderMessages = () => {
    const {messages} = this.state;
    const {messagesFilter} = this.activeTab();
    const filteredMessages = messages.filter(messagesFilter);

    return filteredMessages.map(this.renderMessage);
  };

  renderChat(chatStyles, scrollBarStyles) {
    const {activeTabIndex} = this.state;
    const {isAudience} = this.props;

    return (
      <div className="chat">
        <div className="chat__header" style={chatStyles}>
          <div className="icon icon-commenting icon_22"/>
          <div className="icon icon-angle-double-right chat__close" onClick={this.onChatToggle}/>
        </div>
        <div className="chat__content">
          {!isAudience && <ChatTabs
            onTabChanged={this.onTabChanged}
            tabs={Chat.tabs}
            activeTabIndex={activeTabIndex}
          />}
          <div className="tabs-panes">
            <div className="tabs-panes__pane tabs-panes__pane_active">
              <MessageField sendMessage={this.onSendMessage}/>
              <div
                className="chat__messages-scroll custom-overflow"
                style={scrollBarStyles}
                ref={(el) => {this.chatScroll = el;}}
              >
                <div className="chat__messages-wrapper chat__messages-wrapper_gap-top_m chat__messages-wrapper_size_s">
                  {this.renderMessages()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderToggler = (theme, isHidden) => (
    <div
      className="chat-toggle"
      onClick={this.onChatToggle}
      style={{
        backgroundColor: theme.primaryColor,
        visibility: isHidden && 'hidden',
      }}
    >
      <Icon iconSize="22" glyph="commenting"/>
      <div>CHAT</div>
    </div>
  );

  render() {
    const {isDisplayed} = this.props;
    return (
      <ThemeContext.Consumer>
        {(theme) => {
          const chatStyles = {
            backgroundColor: theme.secondaryColor,
          };
          const scrollBarStyles = {color: theme.primaryColor};

          return (
            <Fragment>
              {isDisplayed && this.renderChat(chatStyles, scrollBarStyles)}
              {this.renderToggler(theme, isDisplayed)}
            </Fragment>
          );
        }}
      </ThemeContext.Consumer>
    );
  }
}
